import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';

import IconButton from '@/components/Button/IconButton';
import {
  Sheet as SheetRoot,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/cn';

// ---------------------------------------------------------------------------
// Sheet コンポーネント
// ---------------------------------------------------------------------------

type Side = 'top' | 'right' | 'bottom' | 'left';

interface SheetProps {
  /** Sheet のタイトル（アクセシビリティ的に必須項目だがデフォルトでは非表示設定にしている） */
  title: string;
  /** タイトルを画面上に表示するかどうか（デフォルト: false、false の場合は sr-onlyで非表示） */
  showTitle?: boolean;
  /** タイトルに適用する追加の className */
  titleClassName?: string;
  /** Sheet を開くトリガー要素 */
  trigger: ReactNode;
  /** Sheet がスライドインする方向（デフォルト: bottom） */
  side?: Side;
  /** Sheet の高さ（top/bottom の場合）。CSS の値を指定（デフォルト: 80vh） */
  height?: string;
  /** Sheet の幅（left/right の場合）。CSS の値を指定（デフォルト: 80vw） */
  width?: string;
  /** コンテンツ部分に適用する追加の className */
  contentClassName?: string;
  /** コンテンツ内のクリックでシートを閉じるかどうか（デフォルト: false） */
  closeOnContentClick?: boolean;
}

/**
 * シートレイアウトコンポーネント
 *
 * 開閉状態は Radix の Sheet が内部で管理するため、手動の state 管理は不要です。
 * `trigger` に渡した要素をクリックすると Sheet が開きます。
 * `side` で Sheet がスライドインする方向を指定できます。
 *
 * @example
 * ```tsx
 * <Sheet
 *   title="ナビゲーション"
 *   side="bottom"
 *   trigger={
 *     <IconButton
 *       icon="ListFilterIcon"
 *       className="absolute top-8 left-0 z-40"
 *     />
 *   }
 * >
 *   <NavigationCard sections={sections} />
 * </Sheet>
 * ```
 */
export function Sheet({
  title,
  showTitle = false,
  titleClassName,
  children,
  trigger,
  side = 'bottom',
  height = '80vh',
  width = '80vw',
  contentClassName,
  closeOnContentClick = false,
}: PropsWithChildren<SheetProps>) {
  const isVertical = side === 'top' || side === 'bottom';
  const closeRef = useRef<HTMLButtonElement>(null);

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    // リンクやボタンのクリックで Radix の SheetClose を発火させて閉じる
    const target = e.target as HTMLElement;
    if (target.closest('a, button')) {
      closeRef.current?.click();
    }
  }, []);

  return (
    <SheetRoot>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side={side}
        hideCloseButton
        className={cn('p-0', contentClassName)}
        style={isVertical ? { height } : { width }}
        onClickCapture={closeOnContentClick ? handleContentClick : undefined}
      >
        <SheetTitle className={cn(!showTitle && 'sr-only', titleClassName)}>
          {title}
        </SheetTitle>
        <SheetClose asChild>
          <IconButton
            ref={closeRef}
            icon="XIcon"
            variant="mutedGhost"
            size="Small"
            aria-label="Close"
            className="absolute top-1 right-1"
          />
        </SheetClose>
        <div className="overflow-y-auto p-4">{children}</div>
      </SheetContent>
    </SheetRoot>
  );
}
